import { useCallback, useEffect, useMemo, useRef } from 'react'
import { shallowEqual, useDispatch, useSelector } from 'react-redux'
import { useLazyFetchMetaFiltersQuery } from 'api/meta/getMetaFilters'
import { selectServiceActions } from 'data/meta/metaSelectors'
import { get } from 'lodash'
import T from 'T'
import { defaultValues, PRICED_SERVICE_TYPE } from 'components/configured-service/CreateDraftConfiguredServiceDialog/settings'
import { filterDefaultValues } from 'components/configured-service/CreateDraftConfiguredServiceDialog/filterSettings'
import { RECURRENCE_V2 } from 'settings/constants/service'
import { canUpdateConfiguredServicePrice } from 'data/permissions/permissionsSelectors'
import { setAccountId, setLocationId, resetCSD } from 'slices/configuredService/createDraftCSDSlice'
import { getUniqueRecurrenceOptions } from 'components/configured-service/EditServiceRecurrenceDialog/utils/recurrenceOptionsHelper'

export function useCreateDraftCSDInit({ isOpen, accountId, locationId, isDesktop, methods, selectedPricedService }) {
  const dispatch = useDispatch()
  const canEditPrice = useSelector(canUpdateConfiguredServicePrice, shallowEqual)

  const [fetchMetaFilters, { data: metaFiltersData }] = useLazyFetchMetaFiltersQuery()
  const actionList = useMemo(() => selectServiceActions(metaFiltersData), [metaFiltersData])
  const recurrenceOptions = useMemo(() => getUniqueRecurrenceOptions(metaFiltersData), [metaFiltersData])

  const resetMapFiltersRef = useRef(null)
  const handleRegisterFiltersReset = useCallback(cb => {
    resetMapFiltersRef.current = typeof cb === 'function' ? cb : null
  }, [])

  const { register, setValue, watch, reset, formState } = methods
  const quantity = watch('quantity')

  const isSelectedPricedServiceOnRequest = useMemo(
    () => selectedPricedService?.serviceType === RECURRENCE_V2.ON_REQUEST,
    [selectedPricedService]
  )

  const resetDialogState = useCallback(() => {
    if (typeof resetMapFiltersRef.current === 'function') {
      resetMapFiltersRef.current()
    }
    reset({
      ...defaultValues,
      serviceList: { ...filterDefaultValues },
      location: null,
      serviceStartDate: new Date(),
      onRequestAction: null,
      forPublish: false,
    })
    dispatch(resetCSD())
  }, [reset, dispatch])

  useEffect(() => {
    if (isOpen) {
      fetchMetaFilters({})
    }
  }, [isOpen, fetchMetaFilters])

  useEffect(() => {
    if (isOpen) {
      resetDialogState()
    }
  }, [isOpen, resetDialogState])

  useEffect(() => {
    if (isOpen && accountId) {
      dispatch(setAccountId(accountId))
    }
  }, [isOpen, accountId, dispatch])

  useEffect(() => {
    setValue('serviceList.recurrenceType', PRICED_SERVICE_TYPE.RECURRING)
  }, [isDesktop, setValue])

  useEffect(() => {
    if (isOpen && locationId) {
      dispatch(setLocationId(locationId))
    }
  }, [isOpen, locationId, dispatch])

  useEffect(() => {
    register('pricedServiceId', { required: T.REQUIRED_HELPER_TEXT })
  }, [register])

  useEffect(() => {
    if (selectedPricedService && canEditPrice) {
      if (!formState?.dirtyFields?.price) {
        setValue('price', get(selectedPricedService, 'value', 0) * (quantity || 0), { shouldValidate: true })
      }
    }
  }, [selectedPricedService, setValue, quantity, formState?.dirtyFields?.price, canEditPrice])

  useEffect(() => {
    if (!isSelectedPricedServiceOnRequest) {
      setValue('onRequestAction', null)
      return
    }
    const defaultActionType = selectedPricedService?.actionType
    if (defaultActionType && Array.isArray(actionList)) {
      const match = actionList.find(a => a?.actionType === defaultActionType)
      setValue('onRequestAction', match || null)
    }
  }, [isSelectedPricedServiceOnRequest, selectedPricedService, actionList, setValue])

  return {
    metaFiltersData,
    actionList,
    recurrenceOptions,
    selectedPricedService,
    canEditPrice,
    handleRegisterFiltersReset,
    resetDialogState,
  }
}
